import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { exportToCsv } from "shared/common/utils/export";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  INSTANCE_DRIFT_EXPORT_COLUMNS,
  toInstanceDriftExportRow,
  type ExportRecord,
} from "../infrastructureExports";
import {
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * View store for the Infrastructure → Deployment Status (Deployment Drift &
 * Go-Live Readiness) view.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. The read-only `instance-drift` service is read through the
 * registered ServiceStore (see serviceStores/infrastructure.stores.ts); `rows`
 * is a computed view over its entity cache (keyed by `instanceId`, the natural
 * key — these derived rows have no separate `id`).
 *
 * The API service registered at `instance-drift` returns a paginated
 * `{ total, data }` envelope. Each row reports a hosted client instance's
 * running container image digest (`runningDigest`, from
 * `client_instances.currentDigest`) against the digest it is expected to be
 * pinned at (`expectedDigest`, from `client_instances.expectedDigest`), plus a
 * derived `driftStatus`. The API computes that status with a pure
 * `computeDriftStatus()`: `unknown` when either digest is missing/empty,
 * `in-sync` when the two trimmed digests are equal, `out-of-sync` otherwise.
 * The Deployment Status view surfaces this so the team can see, at a glance,
 * which client deployments have drifted from their pinned release before
 * go-live.
 */

/** Derived deployment-drift classification for a hosted client instance. */
export type DriftStatus = "in-sync" | "out-of-sync" | "unknown";

export const DRIFT_STATUSES: DriftStatus[] = [
  "in-sync",
  "out-of-sync",
  "unknown",
];

/** UI metadata per drift status: label + MUI chip color. */
export const DRIFT_STATUS_META: Record<
  DriftStatus,
  { label: string; color: "success" | "error" | "default" }
> = {
  "in-sync": { label: "In Sync", color: "success" },
  "out-of-sync": { label: "Out of Sync", color: "error" },
  unknown: { label: "Unknown", color: "default" },
};

export const driftStatusLabel = (status?: string): string =>
  (status && DRIFT_STATUS_META[status as DriftStatus]?.label) || status || "";

export type DriftStatusFilter = "all" | DriftStatus;

/** Sortable columns of the drift table. */
export type DriftSortKey = "instance" | "role" | "client" | "status";

/** All drift columns are text — every one starts A→Z on first click. */
const DESC_FIRST: ReadonlySet<DriftSortKey> = new Set();

/** The container role a client instance plays in a deployment. */
export type InstanceRole = "api" | "web" | "unknown";

/**
 * One client instance's deployment-drift summary as the API serialises it.
 * `clientId` / `projectId` / `name` / `role` and both digests may be null when
 * the underlying `client_instances` row has not recorded them yet.
 */
export interface InstanceDrift {
  /** `client_instances` UUID. */
  instanceId: string;
  clientId: string | null;
  projectId: string | null;
  name: string | null;
  role: InstanceRole | string | null;
  /** Running container image digest (from `client_instances.currentDigest`). */
  runningDigest: string | null;
  /** Expected/pinned image digest (from `client_instances.expectedDigest`). */
  expectedDigest: string | null;
  driftStatus: DriftStatus | string;
  // Registry-sweep tracking — the API emits these on every drift row; they
  // were previously dropped by this type, so the page could not answer
  // "is a newer image in ACR?" alongside "does running match expected?".
  updateAvailable?: boolean;
  latestImageRef?: string | null;
  latestDigest?: string | null;
  updateAvailableSince?: string | null;
}

class InstanceDriftStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;

  // Header filters
  searchQuery = "";
  statusFilter: DriftStatusFilter = "all";

  // Column sort — CLIENT-SIDE, and it stays that way on purpose.
  //
  // As of 2026-08-16 the `instance-drift` service DOES accept `$sort` (over the
  // whitelist in DRIFT_SORTABLE_COLUMNS: name/clientId/role/updateAvailable/
  // updateAvailableSince) and orders in SQL, defaulting to `containerName ASC`
  // with no `$sort` at all. So the base order is already deterministic
  // server-side and this sort is no longer load-bearing for that.
  //
  // It is still load-bearing for correctness, because only two of the four
  // sortable COLUMNS map to something the database can order:
  //   instance → containerName  ✓ DB-backed
  //   role     → role           ✓ DB-backed
  //   client   → the resolved client NAME, joined in the browser from the
  //              global ClientStore. SQL can only order by the raw clientId
  //              UUID, which is a different (and useless) order.
  //   status   → driftStatus, computed AFTER the read by the API's pure
  //              computeDriftStatus(); there is no column to order by.
  // Pushing the two DB-backed keys to the server would split one sort across
  // two layers, for no gain: `load()` fetches the whole set unpaged, so the
  // rows being ordered are already all in memory.
  sortKey: DriftSortKey = "instance";
  sortDir: SortDir = "asc";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `instance-drift` ServiceStore. */
  private get driftService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("instance-drift") ?? this.rootStore.instanceDriftStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /**
   * Drift rows. Per the MobX rule, entities live in the ServiceStore cache
   * (populated by `find`), not in this ViewStore — this is a computed view.
   */
  get rows(): InstanceDrift[] {
    return (this.driftService?.entities ?? []) as unknown as InstanceDrift[];
  }

  /** The global ClientStore (the full tenant list) — name resolution only. */
  private get globalClientStore():
    | {
        clients?: Array<{ id: string; name?: string }>;
        fetchClients?: () => Promise<void>;
      }
    | undefined {
    return (this.rootStore as Record<string, unknown>).clientStore as
      | {
          clients?: Array<{ id: string; name?: string }>;
          fetchClients?: () => Promise<void>;
        }
      | undefined;
  }

  /** Memoized id→name map over the global client list (O(1) per-row lookups). */
  private get clientNameById(): Map<string, string> {
    const map = new Map<string, string>();
    for (const client of this.globalClientStore?.clients ?? []) {
      if (client.name) map.set(client.id, client.name);
    }
    return map;
  }

  /**
   * Display label for a drift row's owning client. The `instance-drift` service
   * returns only the raw `clientId`, so the name comes from the global client
   * list (same resolution ClientInstances uses); the id is the fallback while
   * that list is still loading.
   */
  clientLabel(clientId?: string | null): string {
    if (!clientId) return "-";
    return this.clientNameById.get(clientId) || clientId;
  }

  // ---- Derived data -------------------------------------------------------

  get filteredRows(): InstanceDrift[] {
    let result = this.rows;

    if (this.statusFilter !== "all") {
      result = result.filter((r) => r.driftStatus === this.statusFilter);
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (r) =>
          r.name?.toLowerCase().includes(q) ||
          r.instanceId?.toLowerCase().includes(q) ||
          r.clientId?.toLowerCase().includes(q) ||
          // The Client column shows the resolved name — search it too.
          this.clientLabel(r.clientId).toLowerCase().includes(q) ||
          r.role?.toLowerCase().includes(q) ||
          driftStatusLabel(r.driftStatus).toLowerCase().includes(q)
      );
    }

    return result;
  }

  /**
   * The rows the table renders: filters, then the active column sort. Nulls
   * (e.g. a row with no client) sort last in either direction.
   */
  get sortedRows(): InstanceDrift[] {
    const key = this.sortKey;
    const valueOf = (r: InstanceDrift): SortValue => {
      switch (key) {
        case "instance":
          // Mirror the cell: the container name, else the raw instance id.
          return textValue(r.name ?? r.instanceId);
        case "role":
          return textValue(r.role);
        case "client":
          // Mirror the cell: the resolved client name, else the raw id.
          return textValue(r.clientId ? this.clientLabel(r.clientId) : null);
        case "status":
          return textValue(driftStatusLabel(r.driftStatus));
      }
    };
    return sortRows(this.filteredRows, valueOf, this.sortDir);
  }

  get totalCount(): number {
    return this.rows.length;
  }

  /** How many rows survive the header filters — what the table renders. The
   *  summary cards count the whole fleet, so the body captions the difference. */
  /** True when a search or status filter is set. */
  get hasActiveFilter(): boolean {
    return this.searchQuery.trim().length > 0 || this.statusFilter !== "all";
  }

  get filteredCount(): number {
    return this.filteredRows.length;
  }

  /** True when the table is showing less than the whole loaded set. */
  get isFiltered(): boolean {
    return this.filteredCount !== this.totalCount;
  }

  get outOfSyncCount(): number {
    return this.rows.filter((r) => r.driftStatus === "out-of-sync").length;
  }

  get inSyncCount(): number {
    return this.rows.filter((r) => r.driftStatus === "in-sync").length;
  }

  get unknownCount(): number {
    return this.rows.filter((r) => r.driftStatus === "unknown").length;
  }

  // ---- CSV export ---------------------------------------------------------

  /**
   * The drift report as flat CSV records — exactly what is ON SCREEN, in table
   * order (`sortedRows` = header filters, then the active column sort).
   *
   * This is the release-conversation artifact: full digests (the table
   * abbreviates them), the resolved client name (the service returns only a
   * raw clientId), and the derived drift verdict, in one file.
   */
  get exportRows(): ExportRecord[] {
    return this.sortedRows.map((row) =>
      toInstanceDriftExportRow(
        row,
        this.clientLabel(row.clientId),
        driftStatusLabel(row.driftStatus)
      )
    );
  }

  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  /**
   * Download the on-screen drift rows as CSV. Returns false rather than
   * throwing on an empty set (the toolkit throws there); the header also
   * disables the button on {@link canExport}.
   */
  exportCsv(): boolean {
    const rows = this.exportRows;
    if (rows.length === 0) return false;
    try {
      exportToCsv(rows, INSTANCE_DRIFT_EXPORT_COLUMNS, {
        fileName: `deployment-drift-${new Date().toISOString().slice(0, 10)}`,
      });
      return true;
    } catch (err) {
      console.error("Deployment drift CSV export failed:", err);
      return false;
    }
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setStatusFilter(filter: DriftStatusFilter) {
    this.statusFilter = filter;
  }

  /** Column-header click: same column toggles direction, a new one starts A→Z. */
  setSort(key: DriftSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  // ---- Loading ------------------------------------------------------------

  async load() {
    this.loading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `rows` reads.
      //
      // STILL A BARE QUERY, for a reason that changed on 2026-08-16.
      //
      // History: the schema is `additionalProperties: false`, and it originally
      // allowed only clientId/projectId — so an earlier `$limit: 200` failed
      // every find with "validation failed" and the whole view was blank. It
      // now accepts `$limit` (1..500) and `$sort`, and orders/caps in SQL.
      //
      // We deliberately still send NEITHER:
      //   $sort  — the API already defaults to `containerName ASC`, which is
      //            the same base order this store's default sort key produces,
      //            and two of the four UI sort keys cannot be expressed in SQL
      //            at all (see the sortKey comment above). Sending one would
      //            add a wire dependency and change nothing on screen.
      //   $limit — a drift report is a fleet-wide answer to "is anything
      //            running the wrong image?", and a capped answer to that
      //            question is a wrong answer. Worse, it would be an UNDETECTABLE
      //            cap: the service returns `{ total: data.length }` — its
      //            `total` is computed AFTER the limit is applied, so a
      //            truncated response is indistinguishable from a complete one
      //            and no "Showing N of M" caption could ever be honest. The
      //            fleet is bounded by the number of managed containers (two
      //            per client), so the unpaged read is the correct one.
      // If the fleet ever outgrows an unpaged read, the fix is a real
      // server-side `total` (count before limit), NOT a `$limit` here.
      // findOrThrow: a swallowed failure rendered the all-clear "No
      // deployment drift to report" with zero tiles.
      await findOrThrow(this.driftService, { query: {} });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load deployment drift");
      });

      console.error("Failed to load deployment drift:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Alias used by header refresh / post-write callbacks. */
  async reload() {
    await this.load();
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.searchQuery = "";
    this.statusFilter = "all";
    this.sortKey = "instance";
    this.sortDir = "asc";
  }
}

export default InstanceDriftStore;
